import React from 'react';
import './index.css';

const FIELD_LABELS = {
	givenName: 'First Name',
	familyName: 'Last Name',
	middleName: 'Middle Name',
	fullName: 'Full Name',
	name: 'Name',
	displayName: 'Display Name',
	company: 'Company',
	jobTitle: 'Job Title',
	department: 'Department',
	emails: 'Emails',
	email: 'Email',
	emailAddresses: 'Emails',
	phones: 'Phones',
	phone: 'Phone',
	phoneNumbers: 'Phones',
	addresses: 'Address',
	address: 'Address',
	postalAddresses: 'Address',
	notes: 'Notes',
	note: 'Note',
	groups: 'Labels',
	group: 'Label',
	picture: 'Photo',
	website: 'Website'
};

const resolveLabel = (val, groupNames = {}, reduxLabels = []) => {
	if (!val) return '';
	const cleanId = String(val).includes(':') ? String(val).split(':')[0] : String(val);
	if (cleanId === '6') return 'My Contacts';
	if (cleanId === 'starred') return 'Starred in Android';
	if (groupNames && groupNames[cleanId]) return groupNames[cleanId];
	if (groupNames && groupNames[val]) return groupNames[val];
	const found = reduxLabels.find(l => l.groupId === cleanId || l.id === cleanId || l.groupId === val);
	if (found && (found.name || found.title)) return found.name || found.title;
	return cleanId;
};

const formatValue = (val, key, groupNames, reduxLabels) => {
	if (val === null || val === undefined || val === '') {
		return <em className="text-muted">(empty)</em>;
	}
	if (key === 'group' || key === 'groups') {
		if (Array.isArray(val)) {
			const uniqueLabels = Array.from(new Set(
				val.map(v => resolveLabel(v, groupNames, reduxLabels)).filter(Boolean)
			));
			return uniqueLabels.map((lbl, i) => (
				<span key={i} className="diff-badge badge badge-info badge-pill mr-1">
					<i className="fa fa-tag mr-1" />
					{lbl}
				</span>
			));
		}
		return (
			<span className="diff-badge badge badge-info badge-pill">
				<i className="fa fa-tag mr-1" />
				{resolveLabel(val, groupNames, reduxLabels)}
			</span>
		);
	}
	if (typeof val === 'string') {
		const trimmed = val.trim();
		if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
			try {
				const parsed = JSON.parse(trimmed);
				return formatValue(parsed, key, groupNames, reduxLabels);
			} catch (e) {
				// Keep raw string
			}
		}
		return val;
	}
	if (typeof val === 'object') {
		if (Array.isArray(val)) {
			return val.map((v, i) => (
				<span key={i} className="diff-badge">
					{typeof v === 'object' ? (v.value || v.number || v.address || v.formattedAddress || v.name || JSON.stringify(v)) : String(v)}
				</span>
			));
		}
		return typeof val === 'object' ? (val.value || val.number || val.address || val.formattedAddress || val.name || JSON.stringify(val)) : String(val);
	}
	return String(val);
};

export default function DiffViewer({ item, groupNames = {}, reduxLabels = [] }) {
	if (!item) return null;

	const { changeType, actionType, changes, snapshot, previousSnapshot } = item;

	// For CONTACT_CREATED
	if (changeType === 'CONTACT_CREATED') {
		const entry = item.currentContactEntry || snapshot;
		const contactGroups = entry?.groups || item.groups || [];
		const resolvedGroups = Array.from(new Set(
			contactGroups
				.map(g => resolveLabel(g, groupNames, reduxLabels))
				.filter(name => name && name !== 'My Contacts')
		));

		return (
			<div className="diff-viewer diff-created">
				<div className="diff-header text-success mb-2">
					<span className="badge badge-success mr-2">New Contact</span>
					<strong>Initial Profile Details</strong>
				</div>
				<div className="diff-grid mt-2">
					{entry && (
						<>
							{(entry.givenName || entry.familyName) && (
								<div className="diff-field-row">
									<span className="diff-field-name">Name:</span>
									<span className="diff-val-added font-weight-bold">
										{[entry.givenName, entry.middleName, entry.familyName].filter(Boolean).join(' ')}
									</span>
								</div>
							)}
							{(entry.jobTitle || entry.company) && (
								<div className="diff-field-row">
									<span className="diff-field-name">Job / Company:</span>
									<span className="diff-val-added">
										{entry.jobTitle && <span>{entry.jobTitle}</span>}
										{entry.jobTitle && entry.company && <span> at </span>}
										{entry.company && <strong>{entry.company}</strong>}
									</span>
								</div>
							)}
							{(() => {
								const emailsList = (entry.emailAddresses && entry.emailAddresses.length > 0)
									? entry.emailAddresses
									: (entry.emails && entry.emails.length > 0)
										? entry.emails
										: (entry.primaryEmail ? [{ address: entry.primaryEmail }] : []);
								if (!emailsList || emailsList.length === 0) return null;
								return (
									<div className="diff-field-row">
										<span className="diff-field-name">Emails:</span>
										<span className="diff-val-added">
											{emailsList.map((e, idx) => (
												<span key={idx} className="diff-badge mr-1">
													<i className="fa fa-envelope-o mr-1" />
													{e.value || e.address || e}
												</span>
											))}
										</span>
									</div>
								);
							})()}
							{(() => {
								const phonesList = (entry.phoneNumbers && entry.phoneNumbers.length > 0)
									? entry.phoneNumbers
									: (entry.phones && entry.phones.length > 0)
										? entry.phones
										: (entry.primaryPhone ? [{ number: entry.primaryPhone }] : []);
								if (!phonesList || phonesList.length === 0) return null;
								return (
									<div className="diff-field-row">
										<span className="diff-field-name">Phones:</span>
										<span className="diff-val-added">
											{phonesList.map((p, idx) => (
												<span key={idx} className="diff-badge mr-1">
													<i className="fa fa-phone mr-1" />
													{p.number || p.value || p}
												</span>
											))}
										</span>
									</div>
								);
							})()}
							{resolvedGroups.length > 0 && (
								<div className="diff-field-row">
									<span className="diff-field-name">Labels:</span>
									<span className="diff-val-added">
										{resolvedGroups.map((lbl, idx) => (
											<span key={idx} className="badge badge-info badge-pill mr-1">
												<i className="fa fa-tag mr-1" />
												{lbl}
											</span>
										))}
									</span>
								</div>
							)}
							{(entry.note || changes?.note) && (
								<div className="diff-field-row">
									<span className="diff-field-name">Note:</span>
									<span className="diff-val-added">
										{entry.note || (changes?.note?.newValue ? formatValue(changes.note.newValue, 'note', groupNames, reduxLabels) : '')}
									</span>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		);
	}

	// For CONTACT_DELETED
	if (changeType === 'CONTACT_DELETED') {
		const entry = snapshot || previousSnapshot || item.currentContactEntry;
		const contactGroups = entry?.groups || item.groups || [];
		const resolvedGroups = Array.from(new Set(
			contactGroups
				.map(g => resolveLabel(g, groupNames, reduxLabels))
				.filter(name => name && name !== 'My Contacts')
		));

		return (
			<div className="diff-viewer diff-deleted">
				<div className="diff-header text-danger mb-2">
					<span className="badge badge-danger mr-2">Deleted</span>
					<strong>Contact Snapshot Before Deletion</strong>
				</div>
				{entry ? (
					<div className="diff-grid mt-2 text-muted">
						{(entry.givenName || entry.familyName) && (
							<div className="diff-field-row">
								<span className="diff-field-name">Name:</span>
								<del className="diff-val-removed font-weight-bold">
									{[entry.givenName, entry.middleName, entry.familyName].filter(Boolean).join(' ')}
								</del>
							</div>
						)}
						{(entry.jobTitle || entry.company) && (
							<div className="diff-field-row">
								<span className="diff-field-name">Job / Company:</span>
								<del className="diff-val-removed">
									{entry.jobTitle && <span>{entry.jobTitle}</span>}
									{entry.jobTitle && entry.company && <span> at </span>}
									{entry.company && <strong>{entry.company}</strong>}
								</del>
							</div>
						)}
						{entry.emails && entry.emails.length > 0 && (
							<div className="diff-field-row">
								<span className="diff-field-name">Emails:</span>
								<span className="diff-val-removed">
									{entry.emails.map((e, idx) => (
										<del key={idx} className="diff-badge mr-1">
											{e.value || e.address || e}
										</del>
									))}
								</span>
							</div>
						)}
						{entry.phones && entry.phones.length > 0 && (
							<div className="diff-field-row">
								<span className="diff-field-name">Phones:</span>
								<span className="diff-val-removed">
									{entry.phones.map((p, idx) => (
										<del key={idx} className="diff-badge mr-1">
											{p.number || p.value || p}
										</del>
									))}
								</span>
							</div>
						)}
						{resolvedGroups.length > 0 && (
							<div className="diff-field-row">
								<span className="diff-field-name">Labels:</span>
								<span className="diff-val-removed">
									{resolvedGroups.map((lbl, idx) => (
										<span key={idx} className="badge badge-secondary badge-pill mr-1">
											<i className="fa fa-tag mr-1" />
											{lbl}
										</span>
									))}
								</span>
							</div>
						)}
					</div>
				) : (
					<span className="text-muted small">No prior snapshot available for this contact.</span>
				)}
			</div>
		);
	}

	// For CONTACT_MOVED
	if (changeType === 'CONTACT_MOVED') {
		let oldVal = null;
		let newVal = null;
		if (item.structuredChanges) {
			const moveChange = item.structuredChanges.find(c => c.fieldName === 'group');
			if (moveChange) {
				oldVal = moveChange.oldValue;
				newVal = moveChange.newValue;
			}
		}
		if (!oldVal && !newVal && changes?.group) {
			oldVal = changes.group.oldValue;
			newVal = changes.group.newValue;
		}

		const oldLabelName = oldVal ? resolveLabel(oldVal, groupNames, reduxLabels) : 'Unlabeled';
		const newLabelName = newVal ? resolveLabel(newVal, groupNames, reduxLabels) : 'Unlabeled';

		return (
			<div className="diff-viewer diff-moved">
				<div className="diff-header text-info mb-2">
					<span className="badge badge-info mr-2">Moved</span>
					<strong>Label Reassignment</strong>
				</div>
				<div className="diff-move-details p-3 bg-light rounded d-flex align-items-center justify-content-center">
					<span className="diff-old-label px-3 py-2 rounded font-weight-bold">
						<i className="fa fa-tag mr-1" />
						{oldLabelName}
					</span>
					<span className="diff-arrow mx-3 font-weight-bold text-muted fa-lg"> ➔ </span>
					<span className="diff-new-label px-3 py-2 rounded font-weight-bold">
						<i className="fa fa-tag mr-1" />
						{newLabelName}
					</span>
				</div>
			</div>
		);
	}

	// For RESTORE
	if (changeType === 'RESTORE' || changeType === 'CONTACT_RESTORED' || item.actionType === 'RESTORE') {
		const entry = snapshot || item.currentContactEntry;
		const contactGroups = entry?.groups || item.groups || [];
		const resolvedGroups = Array.from(new Set(
			contactGroups
				.map(g => resolveLabel(g, groupNames, reduxLabels))
				.filter(name => name && name !== 'My Contacts')
		));

		return (
			<div className="diff-viewer diff-restored">
				<div className="diff-header text-success mb-2">
					<span className="badge badge-success mr-2">Restored</span>
					<strong>Contact Restored from Archive</strong>
				</div>
				{entry && (
					<div className="diff-grid mt-2 text-muted">
						{(entry.givenName || entry.familyName || entry.displayName) && (
							<div className="diff-field-row">
								<span className="diff-field-name">Name:</span>
								<span className="font-weight-bold text-dark">
									{[entry.givenName, entry.middleName, entry.familyName].filter(Boolean).join(' ') || entry.displayName}
								</span>
							</div>
						)}
						{resolvedGroups.length > 0 && (
							<div className="diff-field-row">
								<span className="diff-field-name">Labels:</span>
								<span>
									{resolvedGroups.map((lbl, idx) => (
										<span key={idx} className="badge badge-success badge-pill mr-1">
											<i className="fa fa-tag mr-1" />
											{lbl}
										</span>
									))}
								</span>
							</div>
						)}
					</div>
				)}
			</div>
		);
	}

	const isRevert = actionType === 'REVERT' || changeType === 'REVERT';

	// For CONTACT_UPDATED or REVERT (default)
	const rawList = item.structuredChanges && item.structuredChanges.length > 0
		? item.structuredChanges
		: (changes ? Object.entries(changes).map(([k, v]) => ({
			fieldName: k,
			action: v?.type || 'MODIFIED',
			oldValue: v?.oldValue,
			newValue: v?.newValue
		})) : []);

	const structuredList = rawList.filter(ch => {
		const oldStr = ch.oldValue != null ? String(ch.oldValue).trim() : '';
		const newStr = ch.newValue != null ? String(ch.newValue).trim() : '';
		return oldStr !== newStr;
	});

	if (structuredList.length === 0) {
		if (isRevert) {
			return (
				<div className="diff-viewer diff-reverted">
					<div className="diff-header text-warning">
						<span className="badge badge-warning mr-2">Reverted</span>
						<strong>Contact Reverted to Prior Snapshot</strong>
					</div>
				</div>
			);
		}
		return (
			<div className="diff-viewer diff-empty">
				<span className="text-muted">Contact updated (no field-level diff recorded)</span>
			</div>
		);
	}

	return (
		<div className={`diff-viewer ${isRevert ? 'diff-reverted' : 'diff-updated'}`}>
			{isRevert && (
				<div className="diff-header text-warning mb-2">
					<span className="badge badge-warning mr-2">Reverted</span>
					<strong>Contact Reverted to Prior State</strong>
				</div>
			)}
			<table className="table table-sm diff-table m-0">
				<thead>
					<tr>
						<th style={{ width: '25%' }}>Field</th>
						<th style={{ width: '37.5%' }}>Previous Value</th>
						<th style={{ width: '37.5%' }}>{isRevert ? 'Reverted Value' : 'New Value'}</th>
					</tr>
				</thead>
				<tbody>
					{structuredList.map((ch, idx) => {
						const fieldKey = ch.fieldName;
						const label = FIELD_LABELS[fieldKey] || fieldKey;
						const rowAction = ch.action || 'MODIFIED';

						return (
							<tr key={idx} className={`diff-row diff-row-${rowAction.toLowerCase()}`}>
								<td className="diff-field-cell">
									<strong>{label}</strong>
									{rowAction === 'ADDED' && <span className="badge badge-success ml-2">+Added</span>}
									{rowAction === 'REMOVED' && <span className="badge badge-danger ml-2">-Removed</span>}
									{rowAction === 'MOVED' && <span className="badge badge-info ml-2">Moved</span>}
								</td>
								<td className="diff-old-cell">
									{ch.oldValue !== null && ch.oldValue !== undefined ? (
										<del className="diff-del">{formatValue(ch.oldValue, fieldKey, groupNames, reduxLabels)}</del>
									) : (
										<span className="text-muted">—</span>
									)}
								</td>
								<td className="diff-new-cell">
									{ch.newValue !== null && ch.newValue !== undefined ? (
										<ins className="diff-ins">{formatValue(ch.newValue, fieldKey, groupNames, reduxLabels)}</ins>
									) : (
										<span className="text-muted">—</span>
									)}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

